/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EmailIcon from "@mui/icons-material/Email";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Switch,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { AllUser } from "../../types/General";
import {
  useChangeUserStatusMutation,
  useLazyDeleteUserIdQuery,
  useLazyGetAllUsersQuery,
  useLazyGetUserCSVQuery,
  useSendMarketingEmailMutation,
} from "../../services/customers";
import { Loader, showError, showToast } from "../../constants";
import { Pagination, WarnModal } from "../../components";
import { isValidInput } from "../../utils/validations";
import useAuth from "../../hooks/useAuth";
import { Permissions } from "../../types/User";
import moment from "moment";

const PAGE_SIZE = 10;
const HEX_ONLY = /^[0-9a-fA-F]+$/;
const OBJECT_ID_LIKE = /^[0-9a-fA-F]{24,}$/;

const looksLikeEncryptedPayload = (value: string) => {
  const raw = String(value || "").trim();
  if (!raw) return false;
  const parts = raw.split(":");
  if (parts.length !== 2) return false;
  const [ivHex, encryptedHex] = parts;
  if (!ivHex || !encryptedHex) return false;
  if (ivHex.length !== 32 || encryptedHex.length < 32) return false;
  return HEX_ONLY.test(ivHex) && HEX_ONLY.test(encryptedHex);
};

const looksLikeSensitiveHash = (value: string) => {
  const raw = String(value || "").trim();
  if (raw.length < 32) return false;
  return HEX_ONLY.test(raw);
};

const sanitizeCustomerValue = (
  value: string | null | undefined,
  type: "name" | "email" | "phone"
) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  if (type === "email") {
    const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
    if (emailLike) return raw;
    if (looksLikeEncryptedPayload(raw) || looksLikeSensitiveHash(raw)) return "";
    return raw;
  }

  if (type === "phone") {
    const digits = raw.replace(/\D/g, "");
    if (digits.length >= 7 && digits.length <= 15) return raw;
    if (looksLikeEncryptedPayload(raw) || looksLikeSensitiveHash(raw)) return "";
    return raw;
  }

  if (looksLikeEncryptedPayload(raw) || looksLikeSensitiveHash(raw)) return "";
  return raw;
};

const getReadablePlanFallback = (user: AllUser) => {
  const rawStatus = String((user as any)?.subscriptionStatus || "").trim().toLowerCase();
  if (rawStatus.includes("trial")) return "Trial";
  if (rawStatus.includes("paid")) return "Paid Plan";
  return "Free";
};

const ManageUsers = () => {
  const userData = useAuth();
  const navigate = useNavigate();

  const [allUsers, setAllUsers] = useState<AllUser[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "blocked">("all");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [emailDialogOpen, setEmailDialogOpen] = useState<boolean>(false);
  const [emailTargetUser, setEmailTargetUser] = useState<AllUser | null>(null);
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailMessage, setEmailMessage] = useState<string>("");

  const [getAllUsersList, { isLoading }] = useLazyGetAllUsersQuery();
  const [getUserCsvMethod] = useLazyGetUserCSVQuery();
  const [deleteUserById, getDeleteUserData] = useLazyDeleteUserIdQuery();
  const [userStatus, getUserStatusData] = useChangeUserStatusMutation();
  const [sendMarketingEmail, sendMarketingEmailData] = useSendMarketingEmailMutation();
  const [hidePermission, setHidePermission] = useState<Permissions | null | undefined>(null);

  const userPermissions = userData?.permissions?.length
    ? JSON.parse(`${userData?.permissions}`)
    : [];

  const checkPermission = () => {
    const permission = userPermissions;
    if (permission?.length) {
      let idx = -1;
      idx = permission?.findIndex((ele: Permissions) => ele?.label === "Manage Customers");
      if (idx > -1) {
        setHidePermission(permission[idx]);
      } else {
        navigate("/permission-denied", { replace: true });
      }
    }
  };

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const getPlanName = (user: AllUser) => {
    const raw =
      (user as any)?.planDetails?.planName ||
      (user as any)?.subscriptionPlan ||
      (user as any)?.subscriptionName ||
      (user as any)?.planName ||
      (user as any)?.subscription ||
      "";
    const planValue =
      raw && typeof raw === "object"
        ? (raw as any)?.planName || (raw as any)?.name || (raw as any)?.title || ""
        : raw;
    const normalized = String(planValue).trim().toLowerCase();
    if (!normalized) return "Free";
    if (normalized.includes("lite")) return "SaverLite";
    if (normalized.includes("pro")) return "SaverPro";
    if (normalized.includes("trial")) return "Trial";
    if (OBJECT_ID_LIKE.test(normalized)) return getReadablePlanFallback(user);
    return String(planValue);
  };

  const getAuthProviderLabel = (user: AllUser) => {
    const rawProvider =
      (user as any)?.authProvider ||
      (user as any)?.signInProvider ||
      (user as any)?.provider ||
      "";
    const normalized = String(rawProvider).trim().toLowerCase();
    if (normalized.includes("google")) return "Google";
    if (normalized.includes("apple")) return "Apple";
    if (normalized.includes("facebook")) return "Facebook";
    if (normalized.includes("phone")) return "Phone";
    if (normalized.includes("email") || normalized.includes("password")) return "Email";

    if ((user as any)?.googleId) return "Google";
    if ((user as any)?.appleId) return "Apple";
    if ((user as any)?.facebookId) return "Facebook";
    if (!(user as any)?.email && ((user as any)?.phone || (user as any)?.phoneNumber)) return "Phone";
    return "Email";
  };

  const getSubscriptionStatus = (user: AllUser) => {
    const rawStatus = String((user as any)?.subscriptionStatus || "").trim().toLowerCase();
    if (rawStatus === "paid_active") return { label: "Paid Active", color: "success" as const };
    if (rawStatus === "paid_canceled") return { label: "Paid Ends Soon", color: "warning" as const };
    if (rawStatus === "paid_expired") return { label: "Paid Expired", color: "default" as const };
    if (rawStatus === "trial") return { label: "Trial", color: "info" as const };
    if (rawStatus === "grace") return { label: "Grace", color: "warning" as const };
    if (rawStatus === "free") return { label: "Free", color: "default" as const };

    const planName = getPlanName(user).toLowerCase();
    if (planName.includes("pro") || planName.includes("lite")) {
      return { label: "Paid", color: "success" as const };
    }
    if (planName.includes("trial")) return { label: "Trial", color: "info" as const };
    return { label: "Free", color: "default" as const };
  };

  const getProfileStatus = (user: AllUser) => {
    const status = Number((user as any)?.profileStatus || 0);
    if (status <= 1) return { label: "Signup Started", color: "warning" as const };
    if (status < 5) return { label: "Profile Incomplete", color: "info" as const };
    return { label: "Complete", color: "success" as const };
  };

  const getUsers = async () => {
    try {
      const response = await getAllUsersList({
        page,
        limit: PAGE_SIZE,
        query: debouncedSearchTerm.trim(),
        status: statusFilter,
        plan: planFilter,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        sortBy,
        sortOrder,
      }).unwrap();

      if (response?.statusCode === 200) {
        setAllUsers(response?.data?.data || []);
        setTotalCount(response?.data?.count || 0);
      } else {
        setAllUsers([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteUserById({ user_id: id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "");
        getUsers();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const updateStatus = async (id: string, state: boolean) => {
    const body = {
      isBlocked: !state,
    };
    try {
      const response = await userStatus({ user_id: id, body }).unwrap();
      if (response?.statusCode === 200) {
        getUsers();
        if (response?.data?.isBlocked) {
          showToast("User has been successfully disabled");
        } else {
          showToast("User has been successfully enabled");
        }
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Unable to update user status");
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await getUserCsvMethod({}).unwrap();
      if (res?.statusCode === 200) {
        window.open(res?.fileUrl || "");
      }
    } catch (error: any) {
      showError(error?.message || "Unable to export CSV");
    }
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setPlanFilter("all");
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortOrder(column === "createdAt" ? "desc" : "asc");
  };

  const openEmailComposer = (user: AllUser | null) => {
    setEmailTargetUser(user);
    setEmailSubject("");
    setEmailMessage("");
    setEmailDialogOpen(true);
  };

  const closeEmailComposer = () => {
    if (sendMarketingEmailData.isLoading) return;
    setEmailDialogOpen(false);
  };

  const handleSendMarketingEmail = async () => {
    if (!emailSubject.trim() || emailSubject.trim().length < 3) {
      showError("Please add a valid subject");
      return;
    }
    if (!emailMessage.trim() || emailMessage.trim().length < 10) {
      showError("Please add at least 10 characters in the email body");
      return;
    }

    const body: any = {
      subject: emailSubject.trim(),
      message: emailMessage.trim(),
      appkey: Date.now(),
    };

    if (emailTargetUser?._id) {
      body.userIds = [emailTargetUser._id];
    } else {
      body.sendToAll = true;
      body.filters = {
        query: debouncedSearchTerm.trim(),
        status: statusFilter,
        plan: planFilter,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        sortBy,
        sortOrder,
      };
    }

    try {
      const response = await sendMarketingEmail({ body }).unwrap();
      const summary = response?.data
        ? `Sent ${response.data.sent}/${response.data.uniqueRecipients} emails`
        : response?.message;
      showToast(summary || response?.message || "Marketing email campaign queued");
      setEmailDialogOpen(false);
    } catch (error: any) {
      showError(error?.data?.message || "Unable to send marketing email");
    }
  };

  const hasActiveFilters = useMemo(
    () =>
      statusFilter !== "all" ||
      planFilter !== "all" ||
      Boolean(fromDate) ||
      Boolean(toDate) ||
      Boolean(debouncedSearchTerm?.trim()),
    [statusFilter, planFilter, fromDate, toDate, debouncedSearchTerm]
  );

  useEffect(() => {
    getUsers();
  }, [debouncedSearchTerm, page, statusFilter, planFilter, fromDate, toDate, sortBy, sortOrder]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, statusFilter, planFilter, fromDate, toDate, sortBy, sortOrder]);

  useEffect(() => {
    if (userData && userPermissions?.length) {
      checkPermission();
    }
  }, [userData]);

  const label = { inputProps: { "aria-label": "Switch demo" } };

  return (
    <MainContainer>
      <Loader
        isLoad={
          isLoading ||
          getUserStatusData?.isLoading ||
          getDeleteUserData?.isLoading ||
          sendMarketingEmailData?.isLoading
        }
      />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">Manage Customers</h1>
        </div>
        <Card className="cards" sx={{ border: "1px solid #e5e7eb" }}>
          <Box className="cards_header" sx={{ flexDirection: "column", gap: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ width: "100%" }}>
              <SearchBar
                placeholder="Search name, email or phone"
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
              <Button className="btn btn_primary" onClick={handleExportCsv} sx={{ minWidth: 150 }}>
                <FileDownloadIcon /> Export CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => openEmailComposer(null)}
                sx={{ minWidth: 210 }}
              >
                Email Customers
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ width: "100%" }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(event: SelectChangeEvent) =>
                    setStatusFilter(event.target.value as "all" | "active" | "blocked")
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="blocked">Blocked</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Plan</InputLabel>
                <Select
                  label="Plan"
                  value={planFilter}
                  onChange={(event: SelectChangeEvent) => setPlanFilter(event.target.value)}
                >
                  <MenuItem value="all">All Plans</MenuItem>
                  <MenuItem value="free">Free</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                  <MenuItem value="saverlite">SaverLite</MenuItem>
                  <MenuItem value="saverpro">SaverPro</MenuItem>
                  <MenuItem value="paid">Any Paid Plan</MenuItem>
                </Select>
              </FormControl>

              <TextField
                size="small"
                type="date"
                label="From"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <Button
                variant="outlined"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
                sx={{ alignSelf: { xs: "stretch", md: "center" } }}
              >
                Reset Filters
              </Button>
            </Stack>
          </Box>

          <TableContainer className="table_container" sx={{ maxHeight: "68vh", overflowX: "auto" }}>
            <Table
              sx={{
                minWidth: 1310,
                tableLayout: "fixed",
                "& th": { whiteSpace: "nowrap" },
                "& td": { verticalAlign: "middle" },
              }}
              aria-label="customers table"
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: 72 }}>S.No</TableCell>
                  <TableCell sortDirection={sortBy === "firstName" ? sortOrder : false} sx={{ width: 180 }}>
                    <TableSortLabel
                      active={sortBy === "firstName"}
                      direction={sortBy === "firstName" ? sortOrder : "asc"}
                      onClick={() => handleSort("firstName")}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === "email" ? sortOrder : false} sx={{ width: 260 }}>
                    <TableSortLabel
                      active={sortBy === "email"}
                      direction={sortBy === "email" ? sortOrder : "asc"}
                      onClick={() => handleSort("email")}
                    >
                      Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === "phone" ? sortOrder : false} sx={{ width: 150 }}>
                    <TableSortLabel
                      active={sortBy === "phone"}
                      direction={sortBy === "phone" ? sortOrder : "asc"}
                      onClick={() => handleSort("phone")}
                    >
                      Phone
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === "createdAt" ? sortOrder : false} sx={{ width: 140 }}>
                    <TableSortLabel
                      active={sortBy === "createdAt"}
                      direction={sortBy === "createdAt" ? sortOrder : "desc"}
                      onClick={() => handleSort("createdAt")}
                    >
                      Joined
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>Profile Status</TableCell>
                  <TableCell sortDirection={sortBy === "plan" ? sortOrder : false} sx={{ width: 130 }}>
                    <TableSortLabel
                      active={sortBy === "plan"}
                      direction={sortBy === "plan" ? sortOrder : "asc"}
                      onClick={() => handleSort("plan")}
                    >
                      Plan
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 130 }}>Auth Provider</TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>Subscription Status</TableCell>
                  <TableCell align="center" sortDirection={sortBy === "isBlocked" ? sortOrder : false} sx={{ width: 160 }}>
                    <TableSortLabel
                      active={sortBy === "isBlocked"}
                      direction={sortBy === "isBlocked" ? sortOrder : "asc"}
                      onClick={() => handleSort("isBlocked")}
                    >
                      Account Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 170,
                      position: "sticky",
                      right: 0,
                      zIndex: 3,
                      background: "#f8fafc",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allUsers?.length ? (
                  allUsers?.map((item, i) => (
                    <TableRow hover key={item?._id}>
                      {(() => {
                        const safeFirstName = sanitizeCustomerValue(item?.firstName, "name");
                        const safeLastName = sanitizeCustomerValue(item?.lastName, "name");
                        const safeEmail = sanitizeCustomerValue(item?.email, "email");
                        const safePhone = sanitizeCustomerValue(item?.phone, "phone");
                        const safeCountryCode = safePhone
                          ? String(item?.countryCode || "").trim()
                          : "";
                        const safeDisplayName = `${safeFirstName || ""} ${safeLastName || ""}`
                          .trim()
                          .replace(/\s+/g, " ");
                        const safeDisplayPhone = safePhone
                          ? `${safeCountryCode ? (safeCountryCode.includes("+") ? "" : "+") + safeCountryCode : ""} ${
                              safePhone || ""
                            }`.trim()
                          : "-";
                        const subscriptionStatus = getSubscriptionStatus(item);
                        const profileStatus = getProfileStatus(item);

                        return (
                          <>
                            <TableCell align="center">{(page - 1) * PAGE_SIZE + i + 1}</TableCell>
                            <TableCell title={safeDisplayName || "-"} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {safeDisplayName || "-"}
                            </TableCell>
                            <TableCell title={safeEmail || "-"} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {safeEmail || "-"}
                            </TableCell>
                            <TableCell title={safeDisplayPhone} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {safeDisplayPhone}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {item?.createdAt ? moment(item?.createdAt).format("DD MMM YYYY") : "-"}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={profileStatus.label}
                                color={profileStatus.color}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={getPlanName(item)}
                                sx={{
                                  fontWeight: 600,
                                  background: "#ecfeff",
                                  color: "#155e75",
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={getAuthProviderLabel(item)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={subscriptionStatus.label}
                                color={subscriptionStatus.color}
                                variant="outlined"
                              />
                            </TableCell>

                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                                <Chip
                                  size="small"
                                  label={item?.isBlocked ? "Blocked" : "Active"}
                                  color={item?.isBlocked ? "error" : "success"}
                                  variant="outlined"
                                />
                                <Switch
                                  {...label}
                                  size="small"
                                  checked={!item?.isBlocked}
                                  onChange={() => updateStatus(item?._id, item?.isBlocked)}
                                />
                              </Stack>
                            </TableCell>

                            <TableCell
                              sx={{
                                position: "sticky",
                                right: 0,
                                zIndex: 1,
                                background: "inherit",
                              }}
                            >
                              <Box className="table_actions">
                                <Tooltip title="Send marketing email">
                                  <IconButton onClick={() => openEmailComposer(item)}>
                                    <EmailIcon />
                                  </IconButton>
                                </Tooltip>
                                <IconButton onClick={() => navigate(`/manageusers/userdetails/${item?._id}`)}>
                                  <VisibilityIcon />
                                </IconButton>
                                {hidePermission?.isEdit || userData?.role === "ADMIN" ? (
                                  <IconButton onClick={() => navigate(`/manageusers/usersform/${item?._id}`)}>
                                    <ModeEditIcon />
                                  </IconButton>
                                ) : null}
                                {hidePermission?.isDelete || userData?.role === "ADMIN" ? (
                                  <IconButton
                                    onClick={() => {
                                      setOpen(true);
                                      setSelectedId(item?._id);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                ) : null}
                              </Box>
                            </TableCell>
                          </>
                        );
                      })()}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={11} sx={{ color: "#051140" }}>
                      No Customers Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        <Pagination module={allUsers} page={page} onPageChange={onPageChange} totalPages={totalPages} />
      </div>
      <Dialog open={emailDialogOpen} onClose={closeEmailComposer} fullWidth maxWidth="sm">
        <DialogTitle>
          {emailTargetUser
            ? `Email ${`${emailTargetUser.firstName || ""} ${emailTargetUser.lastName || ""}`.trim() || "Customer"}`
            : "Email Customers"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "#475569" }}>
            {emailTargetUser
              ? "Send a marketing email to this customer."
              : "Send a marketing email to customers matching current filters and search."}
          </Typography>
          <TextField
            fullWidth
            label="Subject"
            value={emailSubject}
            onChange={(event) => setEmailSubject(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={5}
            label="Email Body"
            value={emailMessage}
            onChange={(event) => setEmailMessage(event.target.value)}
            placeholder="Write your marketing message here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailComposer} disabled={sendMarketingEmailData.isLoading}>
            Cancel
          </Button>
          <Button
            className="btn btn_primary"
            onClick={handleSendMarketingEmail}
            disabled={sendMarketingEmailData.isLoading}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>
      <WarnModal open={open} setOpen={setOpen} handleDelete={() => handleDelete(selectedId)} name="customer" />
    </MainContainer>
  );
};

export default ManageUsers;
